import React, { useEffect, useState } from 'react';
import {
  API_BASE_URL,
  customerFetch,
  setStoredCustomerToken,
} from '../api/config';
import { PageShell } from '../components/Layout';
import { IconDownload, IconSpinner } from '../components/Icons';
import {
  buildFullByKeysPayload,
  generateHtmlReport,
  triggerHtmlOpenAndDownload,
} from '../utils/report';

export default function DownloadsPage() {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [authRequired, setAuthRequired] = useState(true);
  const [authError, setAuthError] = useState('');
  const [loading, setLoading] = useState(true);
  const [downloads, setDownloads] = useState([]);
  const [busyTxnid, setBusyTxnid] = useState('');

  const loadDownloads = async () => {
    setLoading(true);
    setAuthError('');
    try {
      const res = await customerFetch(`${API_BASE_URL}/customer/downloads`);
      if (res.status === 401) {
        setAuthRequired(true);
        setDownloads([]);
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to load');
      setAuthRequired(false);
      setDownloads(Array.isArray(data.downloads) ? data.downloads : []);
    } catch (err) {
      setAuthError(err.message || 'Could not load reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDownloads(); }, []);

  const submitLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    try {
      const res = await customerFetch(`${API_BASE_URL}/customer/session/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, phone }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Login failed');
      if (data.customerToken) setStoredCustomerToken(data.customerToken);
      await loadDownloads();
    } catch (err) {
      setAuthError(err.message || 'Login failed');
    }
  };

  const openReport = async (txnid) => {
    setBusyTxnid(txnid);
    setAuthError('');
    try {
      const res = await customerFetch(
        `${API_BASE_URL}/customer/downloads/${encodeURIComponent(txnid)}/bootstrap`,
        { method: 'POST' }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Could not prepare download');

      const fullBody = buildFullByKeysPayload(data.token, data.ctx || {}, data.records || []);
      if (!fullBody) throw new Error('Missing record keys');

      const fullRes = await fetch(`${API_BASE_URL}/search/full-by-keys`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fullBody),
      });
      if (!fullRes.ok) {
        let msg = 'Could not load records';
        try {
          const errJson = await fullRes.json();
          msg = errJson.error || msg;
        } catch (_) {}
        throw new Error(msg);
      }
      const fullJson = await fullRes.json().catch(() => ({}));
      const fullRecords = Array.isArray(fullJson.results) ? fullJson.results : [];
      if (!fullRecords.length) throw new Error('No records for this purchase');

      const ctx = data.ctx || {};
      const htmlFn = `Kharedivikri_Report_${ctx.query || 'report'}.html`;
      triggerHtmlOpenAndDownload(generateHtmlReport({ records: fullRecords, ctx }), htmlFn);
    } catch (err) {
      setAuthError(err.message || 'Could not open report');
    } finally {
      setBusyTxnid('');
    }
  };

  return (
    <PageShell>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 md:py-14">
        <h1 className="page-title">My Reports</h1>
        <p className="text-ink/60 mt-3 text-sm max-w-lg">
          Sign in with the email and phone used at checkout to re-download past reports.
        </p>

        {authRequired && (
          <form onSubmit={submitLogin} className="mt-8 bg-white border-2 border-forest p-6 space-y-4">
            <h2 className="font-serif text-lg text-forest font-semibold">Sign in</h2>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="field-input" />
            <input type="tel" required value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Mobile" className="field-input" />
            <button type="submit" className="btn-primary">View my reports</button>
          </form>
        )}

        {authError && (
          <p className="mt-4 text-sm font-semibold text-clay border border-clay/30 bg-clay/5 px-4 py-3">{authError}</p>
        )}

        {loading ? (
          <div className="flex justify-center py-16">
            <IconSpinner className="w-8 h-8 text-forest" />
          </div>
        ) : (
          <div className="mt-8 space-y-4">
            {downloads.length === 0 && !authRequired && (
              <p className="text-center text-ink/50 py-12 border-2 border-dashed border-sand">No paid reports yet.</p>
            )}
            {downloads.map((item) => (
              <div key={item.txnid} className="bg-white border-2 border-sand p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <p className="font-semibold text-forest">Property #{item.query || '—'}</p>
                  <p className="text-xs text-ink/50 mt-1">{item.district} / {item.taluka} / {item.village}</p>
                  <p className="text-[11px] text-ink/40 mt-1 font-mono">{item.txnid}</p>
                </div>
                <button
                  type="button"
                  onClick={() => openReport(item.txnid)}
                  disabled={busyTxnid === item.txnid}
                  className="btn-primary shrink-0 disabled:opacity-60"
                >
                  {busyTxnid === item.txnid ? (
                    <IconSpinner className="w-5 h-5" />
                  ) : (
                    <IconDownload className="w-5 h-5" />
                  )}
                  {busyTxnid === item.txnid ? 'Preparing…' : 'Download'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </PageShell>
  );
}
